import { THEME_TAGS } from '@/lib/themeTags';

// ============================================================
// Documentation officielle des balises Zifek.
// Source unique de vérité pour :
//   • le viewer « Documentation des balises » du Theme Builder ;
//   • l'export téléchargeable ZIFEK_TEMPLATE_TAGS.md.
// Le markdown est GÉNÉRÉ à partir de données structurées (sûr, sans
// concaténation d'accents ou de backticks fragiles).
// ============================================================

export const MANDATORY_PAGES = [
  { key: 'head', role: '<title>, <meta>, SEO, Google Fonts, <link rel="stylesheet" href="/style.css">', tags: 'site_name, site_description, site_url' },
  { key: 'header', role: 'En-tête : logo + navigation', tags: 'site_logo, site_name' },
  { key: 'navmenu', role: 'Menu de navigation détaillé', tags: '(liens)' },
  { key: 'footer', role: 'Pied de page : coordonnées, liens, copyright', tags: 'site_logo, site_name, site_description, site_phone, site_email, site_address, year' },
];

export const CONVERSION_TABLE: { from: string; to: string }[] = [
  { from: '{{merchant.name}} / {{shop.name}}', to: '{{site_name}}' },
  { from: '{{merchant.description}}', to: '{{site_description}}' },
  { from: '{{merchant.logo}}', to: '{{site_logo}}' },
  { from: '{{merchant.phone}}', to: '{{site_phone}}' },
  { from: '{{merchant.email}}', to: '{{site_email}}' },
  { from: '{{merchant.address}} / {{merchant.city}} / {{merchant.country}}', to: '{{site_address}}' },
  { from: '{{merchant.about}} / {{merchant.about_title}}', to: '{{site_about}}' },
  { from: '{{merchant.website}}', to: '{{site_url}}' },
  { from: '{{site.copyright}} / {{copyright}}', to: '{{year}}' },
  { from: '{{featured_product.*}}', to: '{{featured_products limit="1"}}' },
  { from: '{{#products}}…{{product.name}}…{{/products}}', to: '{{products}}' },
  { from: '{{product.*}} (page détail)', to: '{{products limit="1"}}' },
  { from: '{{related_products}}', to: '{{featured_products limit="4"}}' },
  { from: '{{#categories}}…{{category.name}}…{{/categories}}', to: '{{products}} (grille native)' },
  { from: '{{#testimonials}}…{{testimonial.*}}…{{/testimonials}}', to: '{{testimonials}}' },
  { from: '{{blog.posts}} / {{post.*}}', to: '{{blog_posts}} (liste) ou {{blog_posts limit="1"}} (article)' },
  { from: '{{#team.members}}…{{member.*}}…{{/team.members}}', to: '{{team}}' },
  { from: '{{contact.*}} + {{actions.send_contact_message}}', to: '{{contact_form}}' },
  { from: '{{homepage.hero.*}}', to: '{{hero_banners}}' },
  { from: '{{cart.*}} / {{cart_item.*}} / {{actions.checkout}}', to: '(géré nativement par la page panier)' },
  { from: '{{customer.*}} / {{context.current_merchant.*}}', to: '(géré nativement par la page compte)' },
  { from: '{{routes.*}} / {{product.url}} / {{post.url}}', to: '(chemin relatif simple : /produits, /panier…)' },
  { from: '{{actions.*}}', to: '(connecté nativement par les blocs et pages Zifek)' },
];

export const BEST_PRACTICES: string[] = [
  'Aucun texte descriptif en dur : toute donnée dynamique passe par une balise.',
  'head, header, navmenu et footer sont OBLIGATOIRES (le site est cassé sans eux).',
  'Pas de JavaScript, pas de fetch(), pas d’appel réseau dans les pages : le moteur gère le rendu.',
  'Les états vides sont gérés par les blocs : ne code jamais d’état vide manuellement.',
  'Pour un thème marketplace, utilise les balises marketplace_*.',
  'Chemins de routes : chemins relatifs simples (/produits, /panier…).',
];

const DETECTED_SYNTAX = [
  { kind: 'Balise pointée', ex: '{{merchant.name}}, {{product.price}}, {{cart.item_count}}', behav: 'Résolue si connue, sinon retirée (affichage vide)' },
  { kind: 'Marqueur de section', ex: '{{#products}}, {{/products}}, {{^foo}}, {{else}}', behav: 'Retirés' },
  { kind: 'Contrôle Liquid', ex: '{% if %}, {% for %}, {% endfor %}', behav: 'Retirés' },
  { kind: 'Balise simple inconnue', ex: '{{annee}}, {{telephone}}', behav: 'Remplacée par rien' },
];

/** Génère le contenu markdown complet de la documentation des balises. */
export function buildZifekTagsMarkdown(): string {
  const lines: string[] = [];
  lines.push('# ZIFEK — DOCUMENTATION COMPLÈTE DES BALISES DE THÈME (Template Tags)');
  lines.push('');
  lines.push('> Le moteur de thème Zifek (TemplateRenderer) remplace chaque balise {{...}} d’une page');
  lines.push('> par les données réelles du commerçant du sous-domaine consulté.');
  lines.push('');
  lines.push('⚠️ Toute balise absente de cette liste s’affiche vide sur le site public.');
  lines.push('⚠️ Zifek n’utilise PAS de syntaxe « Liquid/Shopify » : les balises pointées');
  lines.push('({{merchant.name}}), les boucles manuelles ({{#products}}…{{/products}}) et les blocs');
  lines.push('{% %} sont incompris et retirés au rendu.');
  lines.push('');

  const groups: { title: string; category: 'simple' | 'block' | 'marketplace' }[] = [
    { title: '1. BALISES SIMPLES', category: 'simple' },
    { title: '2. BALISES DE BLOCS', category: 'block' },
    { title: '3. BALISES MARKETPLACE', category: 'marketplace' },
  ];
  for (const g of groups) {
    lines.push(`## ${g.title}`);
    lines.push('');
    lines.push('| Balise | Affiche | Attributs |');
    lines.push('|---|---|---|');
    THEME_TAGS.filter((t) => t.category === g.category).forEach((t) => {
      const attrs = [
        t.supportsLimit ? 'limit="N"' : '',
        ...(t.extraAttrs || []),
      ].filter(Boolean).join(', ');
      lines.push(`| {${`{${t.usage}}`}} | ${t.description} | ${attrs || '—'} |`);
    });
    lines.push('');
  }

  lines.push('## 4. LES 4 PAGES DE STRUCTURE OBLIGATOIRES');
  lines.push('');
  lines.push('| Page (page_key) | Rôle | Balises typiques |');
  lines.push('|---|---|---|');
  MANDATORY_PAGES.forEach((p) => lines.push(`| ${p.key} | ${p.role} | ${p.tags} |`));
  lines.push('');

  lines.push('## 5. DÉTECTION DES BALISES NON-ZIFEK');
  lines.push('');
  lines.push('| Syntaxe détectée | Exemple | Comportement au rendu |');
  lines.push('|---|---|---|');
  DETECTED_SYNTAX.forEach((d) => lines.push(`| ${d.kind} | ${d.ex} | ${d.behav} |`));
  lines.push('');

  lines.push('## 6. TABLE DE CONVERSION (autre moteur → Zifek)');
  lines.push('');
  lines.push('| Balise d’un autre moteur | Balise Zifek à utiliser |');
  lines.push('|---|---|');
  CONVERSION_TABLE.forEach((c) => lines.push(`| ${c.from} | ${c.to} |`));
  lines.push('');

  lines.push('## 7. BONNES PRATIQUES');
  lines.push('');
  BEST_PRACTICES.forEach((b) => lines.push(`* ${b}`));
  lines.push('');

  lines.push('## 8. RAPPEL ANTI-LIQUID');
  lines.push('');
  lines.push('❌ Interdit : {{merchant.name}} · {{product.price}} · {{cart.item_count}} ·');
  lines.push('{{#products}}…{{/products}} · {{else}} · {% if %} · {{routes.products}}');
  lines.push('');
  lines.push('✅ Autorisé : uniquement les balises officielles listées ci-dessus.');
  lines.push('');

  return lines.join('\n');
}

/** Télécharge la documentation des balises au format .md. */
export function downloadZifekTagsDoc(): void {
  const md = buildZifekTagsMarkdown();
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ZIFEK_TEMPLATE_TAGS.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}